import { useState, useEffect } from 'react'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabase'
import { confirmDialog } from '@/lib/confirmDialog'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Input from '@/components/common/Input'
import Badge from '@/components/common/Badge'

function TaskWidget({ team }) {
  const [tasks, setTasks] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [newTaskTitle, setNewTaskTitle] = useState('')
  const [saving, setSaving] = useState(false)

  const storageKey = team?.id ? `taskWidgetState:${team.id}` : null


  useEffect(() => {
    if (team?.id) {
      fetchTasks()
    }
  }, [team?.id])

  useEffect(() => {
    if (!storageKey || typeof window === 'undefined') return

    const saved = sessionStorage.getItem(storageKey)
    if (!saved) return

    try {
      const parsed = JSON.parse(saved)
      if (typeof parsed.showForm === 'boolean') {
        setShowForm(parsed.showForm)
      }
      if (typeof parsed.newTaskTitle === 'string') {
        setNewTaskTitle(parsed.newTaskTitle)
      }
    } catch (error) {
      sessionStorage.removeItem(storageKey)
    }
  }, [storageKey])

  useEffect(() => {
    if (!storageKey || typeof window === 'undefined') return

    sessionStorage.setItem(
      storageKey,
      JSON.stringify({ showForm, newTaskTitle })
    )
  }, [storageKey, showForm, newTaskTitle])

  const fetchTasks = async () => {
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('tasks')
        .select('*')
        .eq('team_id', team.id)
        .order('completed', { ascending: true })
        .order('created_at', { ascending: false })

      if (error) throw error

      setTasks(data || [])
    } catch (err) {
    } finally {
      setLoading(false)
    }
  }

  const handleAddTask = async (e) => {
    e.preventDefault()
    if (!newTaskTitle.trim()) return

    setSaving(true)

    try {
      const { data, error } = await supabase
        .from('tasks')
        .insert({
          team_id: team.id,
          title: newTaskTitle.trim(),
          completed: false
        })
        .select()
        .single()

      if (error) throw error

      setTasks([data, ...tasks])
      setNewTaskTitle('')
      setShowForm(false)
      if (storageKey && typeof window !== 'undefined') {
        sessionStorage.removeItem(storageKey)
      }
    } catch (err) {
      alert('Failed to add task')
    } finally {
      setSaving(false)
    }
  }

  const handleToggleComplete = async (taskId, currentStatus) => {

    try {
      const { error } = await supabase
        .from('tasks')
        .update({ 
          completed: !currentStatus
        })
        .eq('id', taskId)

      if (error) throw error

      setTasks(tasks.map(task => 
        task.id === taskId 
          ? { ...task, completed: !currentStatus }
          : task
      ))
    } catch (err) {
      toast.error('Failed to update task')
    }
  }

  const handleDeleteTask = async (taskId) => {
    const confirmed = await confirmDialog(
      'Delete this task?',
      { confirmText: 'Delete', cancelText: 'Cancel' }
    )
    if (!confirmed) return


    try {
      const { error } = await supabase
        .from('tasks')
        .delete()
        .eq('id', taskId)

      if (error) throw error

      setTasks(tasks.filter(task => task.id !== taskId))
    } catch (err) {
      alert('Failed to delete task')
    }
  }

  // Check if free tier
  const isFree = team?.season_tier === 'free'
  const incompleteTasks = tasks.filter(t => !t.completed)
  const completedTasks = tasks.filter(t => t.completed)

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h2 className="font-heading text-xl font-bold">
            Tasks
          </h2>
          {isFree && <Badge variant="amber">Pro Feature</Badge>}
        </div>
        {!isFree && (
          <Button
            variant="secondary"
            onClick={() => setShowForm(!showForm)}
            disabled={loading}
            className="text-sm px-3 py-1.5"
          >
            {showForm ? 'Cancel' : '+ Add Task'}
          </Button>
        )}
      </div>

      {isFree ? (
        <div className="py-8 text-center">
          <p className="text-brand-slate mb-4">
            Task management is available on the Pro tier.
          </p>
          <Button
            variant="primary"
            onClick={() => window.location.href = '/pricing'}
            className="text-sm"
          >
            Upgrade to Pro
          </Button>
        </div>
      ) : (
        <>
          {showForm && (
            <form onSubmit={handleAddTask} className="mb-4 pb-4 border-b border-border">
              <div className="flex gap-2">
                <Input
                  placeholder="Enter task..."
                  value={newTaskTitle}
                  onChange={(e) => setNewTaskTitle(e.target.value)}
                  disabled={saving}
                  className="flex-1"
                  autoFocus
                />
                <Button
                  type="submit"
                  variant="primary"
                  disabled={saving || !newTaskTitle.trim()}
                  className="text-sm px-4"
                >
                  {saving ? 'Adding...' : 'Add'}
                </Button>
              </div>
            </form>
          )}

          {loading ? (
            <div className="text-center py-8 text-brand-slate">
              Loading tasks...
            </div>
          ) : tasks.length === 0 ? (
            <div className="text-center py-8 text-brand-slate">
              <p className="mb-3">No tasks yet.</p>
              {!showForm && (
                <Button
                  variant="secondary"
                  onClick={() => setShowForm(true)}
                  className="text-sm"
                >
                  Add Your First Task
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {/* Incomplete tasks */}
              {incompleteTasks.map((task) => (
                <div
                  key={task.id}
                  className="flex items-center gap-3 p-3 bg-white border border-border rounded-lg group hover:shadow-sm transition"
                >
                  <input
                    type="checkbox"
                    checked={false}
                    onChange={() => handleToggleComplete(task.id, task.completed)}
                    className="w-5 h-5 text-brand-green focus:ring-brand-green"
                  />
                  <span className="flex-1 text-brand-charcoal">
                    {task.title}
                  </span>
                  <button
                    onClick={() => handleDeleteTask(task.id)}
                    className="text-red-500 opacity-0 group-hover:opacity-100 transition text-sm hover:underline"
                  >
                    Delete
                  </button>
                </div>
              ))}

              {/* Completed tasks */}
              {completedTasks.length > 0 && (
                <div className="pt-4 mt-4 border-t border-border">
                  <p className="text-xs text-brand-slate uppercase font-medium mb-2">
                    Completed ({completedTasks.length})
                  </p>
                  {completedTasks.map((task) => (
                    <div
                      key={task.id}
                      className="flex items-center gap-3 p-3 bg-gray-50 border border-border rounded-lg group hover:shadow-sm transition opacity-60"
                    >
                      <input
                        type="checkbox"
                        checked={true}
                        onChange={() => handleToggleComplete(task.id, task.completed)}
                        className="w-5 h-5 text-brand-green focus:ring-brand-green"
                      />
                      <span className="flex-1 text-brand-slate line-through">
                        {task.title}
                      </span>
                      <button
                        onClick={() => handleDeleteTask(task.id)}
                        className="text-red-500 opacity-0 group-hover:opacity-100 transition text-sm hover:underline"
                      >
                        Delete
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </Card>
  )
}

export default TaskWidget


